// Mirrors iOS RestaurantDailyEarningsView: month selector with chevrons,
// 7-column calendar grid where each day cell shows earnings + order count,
// monthly summary cards, and a bottom-sheet day detail with that day's
// orders and totals.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, DollarSign, ShoppingBag, CalendarCheck,
  TrendingUp, Inbox, Trophy,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import Sheet from '../shared/ui/Sheet';
import { StatusBadge, LoadingView } from '../shared/ui';
import { listenToRestaurantOrders } from '../shared/services/orderService';
import { formatCurrency, formatTime } from '../shared/models';
import {
  WEEKDAY_SHORT, dailyEarningsMap, earningsForDate, startOfDay,
  dayKey, payoutOf,
} from '../components/analytics/analyticsUtils';

// ---------- Day cell (iOS RestaurantDayCell) ----------
function DayCell({ date, earnings, isToday, isSelected, onTap }) {
  const hasEarnings = earnings.totalEarnings > 0;
  let bg = 'bg-gray-100';
  let text = 'text-gray-900';
  let earn = 'text-green-600';
  let count = 'text-gray-400';
  if (isSelected) {
    bg = 'bg-[#FF6B35]';
    text = 'text-white';
    earn = 'text-white';
    count = 'text-white/80';
  } else if (isToday) {
    bg = 'bg-blue-500';
    text = 'text-white';
    earn = 'text-white';
    count = 'text-white/80';
  } else if (hasEarnings) {
    bg = 'bg-green-50';
  }

  return (
    <button
      onClick={onTap}
      className={`h-[70px] w-full rounded-lg flex flex-col items-center justify-center gap-0.5 ${bg} ${
        isSelected ? 'ring-[3px] ring-[#FF6B35]' : isToday ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <span className={`text-sm ${isToday || isSelected ? 'font-bold' : ''} ${text}`}>
        {date.getDate()}
      </span>
      {hasEarnings && (
        <>
          <span className={`text-[10px] leading-none ${earn}`}>{formatCurrency(earnings.totalEarnings)}</span>
          <span className={`text-[9px] leading-none ${count}`}>{earnings.orderCount}</span>
        </>
      )}
    </button>
  );
}

// ---------- Summary card (iOS DailySummaryCard) ----------
function SummaryCard({ title, value, icon: Icon, color, sub = null }) {
  return (
    <div className="bg-gray-50/70 rounded-xl p-3 flex-1">
      <Icon size={20} className={color} />
      <p className="text-lg font-extrabold text-gray-900 mt-1.5 truncate">{value}</p>
      <p className="text-[11px] text-gray-400">{title}</p>
      {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
    </div>
  );
}

// ---------- Day detail sheet (iOS DayEarningsDetailSheet + order list) ----------
function DayDetailSheet({ date, earnings, onClose }) {
  const navigate = useNavigate();
  if (!date || !earnings) return null;
  const fmtDate = date
    ? date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  const Row = ({ label, value, bold = false, green = false }) => (
    <div className="flex items-center justify-between py-1">
      <span className={bold ? 'text-sm font-bold text-gray-900' : 'text-sm text-gray-500'}>{label}</span>
      <span className={`text-sm ${bold ? 'font-bold' : 'font-medium'} ${green ? 'text-green-600' : 'text-gray-800'}`}>
        {value}
      </span>
    </div>
  );

  return (
    <Sheet open onClose={onClose} title="Daily Details" maxHeight="88vh">
      <div className="px-4 pb-6 space-y-5">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-900">{fmtDate}</p>
          <p className="text-sm text-gray-400">Daily Earnings</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <p className="text-[11px] text-gray-400">Total Earnings</p>
            <p className="text-2xl font-extrabold text-green-600">{formatCurrency(earnings.totalEarnings)}</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <p className="text-[11px] text-gray-400">Total Orders</p>
            <p className="text-2xl font-extrabold text-blue-600">{earnings.orderCount}</p>
          </div>
        </div>

        <div>
          <p className="text-sm font-bold text-gray-900 mb-2">Earnings Breakdown</p>
          <div className="bg-gray-50/70 rounded-xl p-3">
            <Row label="Subtotal" value={formatCurrency(earnings.subtotal)} />
            <Row label="Platform Commission (10%)" value={`-${formatCurrency(earnings.subtotal * 0.1)}`} />
            <Row label="Tips" value={formatCurrency(earnings.tips)} />
            <Row label="Delivery Fees" value={formatCurrency(earnings.deliveryFee)} />
            <Row label="Orders" value={`${earnings.orderCount}`} />
            <Row label="Avg per Order" value={formatCurrency(earnings.averageOrderValue)} />
            <div className="border-t border-gray-200 my-1.5" />
            <Row label="Total Earnings" value={formatCurrency(earnings.totalEarnings)} bold green />
          </div>
        </div>

        {earnings.orderCount === 0 ? (
          <div className="flex flex-col items-center py-8 text-gray-400 gap-2">
            <Inbox size={36} className="opacity-40" />
            <p className="text-sm">No orders for this day</p>
          </div>
        ) : (
          <div>
            <p className="text-sm font-bold text-gray-900 mb-2">Orders</p>
            <div className="space-y-2">
              {earnings.orders.map((o) => (
                <button
                  key={o.id}
                  onClick={() => { onClose(); navigate(`/business/orders/${o.id}`); }}
                  className="w-full flex items-center justify-between gap-2 bg-gray-50/70 hover:bg-gray-100 rounded-lg px-3 py-2.5 text-left transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      #{String(o.orderNumber || o.id).slice(0, 8)} · {o.customerName || 'Customer'}
                    </p>
                    <p className="text-[11px] text-gray-400">{formatTime(o.createdAt)} · {o.orderType}</p>
                  </div>
                  <div className="text-right shrink-0 space-y-1">
                    <p className="text-sm font-semibold text-green-600">{formatCurrency(payoutOf(o))}</p>
                    <StatusBadge status={o.status} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ---------- Page ----------
export default function EarningsPage() {
  const { restaurantId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!restaurantId) { setLoading(false); return undefined; }
    const unsub = listenToRestaurantOrders(restaurantId, (list) => {
      setOrders(list);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [restaurantId]);

  const earningsByDay = useMemo(() => dailyEarningsMap(orders), [orders]);

  // Calendar cells for the selected month (leading blanks for the first week).
  const cells = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result = [];
    for (let i = 0; i < first.getDay(); i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(new Date(year, month, d));
    return result;
  }, [selectedMonth]);

  // Monthly summary figures.
  const monthStats = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const days = [...earningsByDay.values()].filter(
      (d) => d.date.getFullYear() === year && d.date.getMonth() === month,
    );
    const total = days.reduce((s, d) => s + d.totalEarnings, 0);
    const count = days.reduce((s, d) => s + d.orderCount, 0);
    const active = days.filter((d) => d.orderCount > 0);
    const best = active.reduce((m, d) => (d.totalEarnings > (m?.totalEarnings ?? -1) ? d : m), null);
    return {
      total,
      count,
      daysActive: active.length,
      avgDaily: active.length ? total / active.length : 0,
      best,
    };
  }, [earningsByDay, selectedMonth]);

  const now = new Date();
  const isCurrentMonth =
    selectedMonth.getFullYear() === now.getFullYear() && selectedMonth.getMonth() === now.getMonth();
  const todayKey = dayKey(now);

  const shiftMonth = (delta) => {
    setSelectedMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  };

  return (
    <div className="pb-6">
      <ChefHeader title="Daily Earnings" />

      {loading ? (
        <LoadingView label="Loading earnings…" />
      ) : (
        <div className="px-4 pt-4 space-y-5">
          {/* Month selector */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center justify-between">
            <button
              onClick={() => shiftMonth(-1)}
              className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-blue-500 active:bg-gray-100"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-lg font-bold text-gray-900">
              {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => shiftMonth(1)}
              disabled={isCurrentMonth}
              className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-blue-500 active:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Calendar grid */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-2">
            <div className="grid grid-cols-7 gap-2">
              {WEEKDAY_SHORT.map((d) => (
                <div key={d} className="text-center text-[11px] font-semibold text-gray-400">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {cells.map((date, i) =>
                date ? (
                  <DayCell
                    key={dayKey(date)}
                    date={date}
                    earnings={earningsForDate(earningsByDay, date)}
                    isToday={dayKey(date) === todayKey}
                    isSelected={selectedDate ? dayKey(date) === dayKey(selectedDate) : false}
                    onTap={() => setSelectedDate(date)}
                  />
                ) : (
                  <div key={`empty-${i}`} className="h-[70px]" />
                ),
              )}
            </div>
          </div>

          {/* Monthly summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
            <h3 className="text-sm font-bold text-gray-900">Monthly Summary</h3>
            <div className="flex gap-3">
              <SummaryCard title="Total Earnings" value={formatCurrency(monthStats.total)} icon={DollarSign} color="text-green-600" />
              <SummaryCard title="Total Orders" value={`${monthStats.count}`} icon={ShoppingBag} color="text-blue-500" />
            </div>
            <div className="flex gap-3">
              <SummaryCard
                title="Best Day"
                value={monthStats.best ? formatCurrency(monthStats.best.totalEarnings) : '$0.00'}
                icon={Trophy}
                color="text-[#FF6B35]"
                sub={monthStats.best
                  ? monthStats.best.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : `${monthStats.daysActive} days active`}
              />
              <SummaryCard
                title="Avg Daily"
                value={formatCurrency(monthStats.avgDaily)}
                icon={TrendingUp}
                color="text-purple-500"
                sub={monthStats.daysActive ? `${monthStats.daysActive} days active` : null}
              />
            </div>
            {monthStats.daysActive > 0 && (
              <p className="text-[11px] text-gray-400 flex items-center gap-1">
                <CalendarCheck size={12} /> {monthStats.daysActive} day{monthStats.daysActive === 1 ? '' : 's'} with orders this month
              </p>
            )}
          </div>
        </div>
      )}

      <DayDetailSheet
        date={selectedDate}
        earnings={selectedDate ? earningsForDate(earningsByDay, selectedDate) : null}
        onClose={() => setSelectedDate(null)}
      />
    </div>
  );
}
